import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  WarningAmberRounded,
  MonitorHeartRounded,
  HistoryRounded,
  BadgeRounded,
  FavoriteRounded,
  PlaceRounded,
  CalculateRounded,
} from "@mui/icons-material";

import SectionCard from "../../molecules/SectionCard";
import InfoRow from "../../atoms/InfoRow";
import ActionButton from "../../atoms/ActionButton";
import FormTextField from "../../atoms/FormTextField";
import PhoneInput from "../../atoms/PhoneInput";
import FormSelect from "../../atoms/FormSelect";
import EmptyState from "../../atoms/EmptyState";
import SaveStatusIndicator from "../../atoms/SaveStatusIndicator";
import { useActiveModules, moduleChecker } from "../../../hooks/useClinicModule";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO, CARD_PAD, CARD_GAP, dashboardSectionSx } from "../../../styles/uiConstants";
import { useUpdatePatientMutation } from "../../../store";
import {
  useListDiagnosesMutation,
  useListAllergiesMutation,
} from "../../../store/systemVariablesApi";
import { useAlert } from "../../../context/AlertProvider";
import FormDatePicker from "../../atoms/FormDatePicker";
import TagPickerDrawer from "./drawers/TagPickerDrawer";
import WhatsAppConsentRow from "./WhatsAppConsentRow";
import BmiCalculatorDrawer from "./drawers/BmiCalculatorDrawer";
import { bmiCategory } from "../../../utils/bmi";
import useTerms from "../../../hooks/useTerms";

// Alerji / kronik hastalık kartlarının gövde yüksekliği — yan yana kartlar
// içeriğe göre birbirini uzatmaz, uzun liste kart içinde kayar.
const TAG_CARD_BODY_HEIGHT = 260;

const cardSx = { ...dashboardSectionSx, display: "flex", flexDirection: "column" };

const ageOf = (birthDay) => {
  if (!birthDay) return null;
  const d = new Date(birthDay);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "0+", "0-"];

const GENDER_OPTIONS = [
  { value: "Erkek", label: "Erkek" },
  { value: "Kadın", label: "Kadın" },
];

const BLOOD_TYPE_OPTIONS = BLOOD_TYPES.map((bt) => ({ value: bt, label: bt }));

const ADDRESS_KEYS = ["city", "district", "fullAddress", "postalCode"];

const buildDiagnosisLabel = (opt) =>
  `${opt?.code || ""} ${opt?.name || ""}`.trim();

// Kayıtlı tanı metni "I10 Esansiyel hipertansiyon" biçimindedir; kod ayrı
// sütunda gösterilir. Kodsuz eski kayıtlar olduğu gibi ad olarak kalır.
const splitDiagnosis = (text) => {
  const m = /^([A-Z]\d{2}(?:\.\d{1,2})?)\s+(.+)$/.exec(String(text || "").trim());
  return m ? { code: m[1], name: m[2] } : { code: null, name: text };
};

// Seçim drawer'ında sunucudan sayfa başına gelen kayıt
const PICKER_PAGE_SIZE = 30;

// Alan değerinin yanında soluk ek bilgi ("45 yaş", "Fazla kilolu")
const FieldHint = ({ children, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      flexShrink: 0,
      fontSize: TYPO.label,
      fontWeight: color ? 600 : 500,
      color: color || COLORS.slate500,
      whiteSpace: "nowrap",
    }}
  >
    {color ? (
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color }} />
    ) : null}
    {children}
  </Box>
);

// InfoRow + yüklenirken alan iskeleti. Bileşen dışında tanımlı: içeride
// tanımlansa her render'da yeni tip olur, input'lar remount olup odak kaybolur.
const Row = ({ label, noBorder, loading, children }) => (
  <InfoRow label={label} noBorder={noBorder}>
    {loading ? <Skeleton variant="text" width="55%" height={26} /> : children}
  </InfoRow>
);

/**
 * PatientWorkspaceTab ortak arayüzü.
 *
 * Yerleşim (detay sekmesi = slate50 zemin + bağımsız kartlar):
 *   1. satır: Kimlik ve İletişim | (Sağlık + Adres)
 *   2. satır: Alerjiler | Aktif kronik | Geçmiş kronik — sabit gövde yüksekliği;
 *      ekleme sağdan açılan seçim drawer'ı ile (`TagPickerDrawer`).
 *
 * @property {"standalone" | "session"} mode
 *   "standalone" — sayfa kendi fetch'ini yapar (patientData prop'u ile).
 *   "session"    — parent (ExaminationSession) veriyi initialData prop'u olarak gönderir,
 *   mutation'lar autoSaveRegistry'e register edilir.
 * @property {string} patientId
 * @property {string} token
 * @property {object} [patientData]              Standalone mode — parent tarafından yüklenen patient
 * @property {(patient: object) => void} [onPatientSaved]
 *   Standalone mode — parent callback; yeni patient state'iyle çağrılır.
 * @property {boolean} [canEdit]                 Read-only görünüm kontrolü
 * @property {object} [initialData]              Session mode — examination payload slice
 * @property {(promise: Promise) => Promise} [registerAutoSave]
 *   Session mode — her mutation.unwrap() bu helper'a sarılır.
 * @property {(status: "idle"|"saving"|"saved"|"error") => void} [onSaveStatusChange]
 *   Session mode — parent'a save durumunu bildirir.
 */
export default function PersonalInfoTab({
  mode,
  patientId,
  token,
  patientData,
  onPatientSaved,
  canEdit = true,
  // Hasta verisi gelene kadar kart çerçeveleri ve başlıklar çizilir, alanlar
  // ve listeler iskelet gösterir.
  loading = false,
  // Kimlik bilgilerini ayrı olarak kilitleyebilmek için flag — muayene
  // session'unda hasta kimlik bilgileri kilitli, sağlık/adres/alerji/hastalık
  // bölümleri editlenebilir kalır.
  generalReadOnly,
  initialData,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  // Modüle göre alanlar (spec §7.2): kronik hastalık kartları anamnezle,
  // kan grubu reçete/geleneksel uygulamayla birlikte anlamlı.
  const hasModule = moduleChecker(useActiveModules());
  const showChronic = hasModule("anamnesis");
  const showBloodType = hasModule("prescription") || hasModule("traditional");
  const { showSuccess, showError } = useAlert();
  const [updatePatient, { isLoading: isSaving }] = useUpdatePatientMutation();
  const [listDiagnoses] = useListDiagnosesMutation();
  const [listAllergies] = useListAllergiesMutation();

  const source = mode === "session" ? initialData : patientData;
  const readOnly = !canEdit;
  const genReadOnly = generalReadOnly ?? readOnly;
  // Mobil uygulamayı kullanan hastanın iletişim bilgileri (telefon, e-posta) klinikte
  // değiştirilemez — hasta uygulamadan kendisi günceller; sunucu da reddeder
  // (user.service › patient-identity). Ad, soyad, TC, doğum tarihi ve cinsiyet klinikte
  // her zaman düzenlenebilir (2026-10-08). Uygulamaya hiç girmemiş hastada hepsi serbest.
  const usesApp = source?.hasLoggedIn !== false;
  const identityReadOnly = genReadOnly;
  const contactReadOnly = genReadOnly || usesApp;

  // ── Section state'leri ─────────────────────────────────────────────
  const [general, setGeneral] = useState({
    name: "",
    surname: "",
    email: "",
    tellNo: "",
    tcNo: "",
    gender: "",
    birthDay: "",
  });

  const [physical, setPhysical] = useState({
    BMI: "",
    bloodType: "",
  });

  const [address, setAddress] = useState({
    city: "",
    district: "",
    fullAddress: "",
    postalCode: "",
  });

  // Otomatik kayıt durumu — sağ üstte sabit gösterge için.
  const [saveStatus, setSaveStatus] = useState("idle");

  const [allergies, setAllergies] = useState([]);
  const [activeDiseases, setActiveDiseases] = useState([]);
  const [pastDiseases, setPastDiseases] = useState([]);

  // Seçim drawer'ı: hangi kart için ("allergy" | "active" | "past") ve açık mı.
  // Kart anahtarı kapanırken de korunur ki kapanış animasyonunda içerik boşalmasın.
  const [picker, setPicker] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bmiDrawerOpen, setBmiDrawerOpen] = useState(false);
  const [bmiSaving, setBmiSaving] = useState(false);
  const openPicker = (key) => {
    setPicker(key);
    setPickerOpen(true);
  };

  // Patient değiştiğinde tüm section state'lerini resetle
  useEffect(() => {
    if (!source) return;
    setGeneral({
      name: source.name || "",
      surname: source.surname || "",
      email: source.email || "",
      tellNo: source.tellNo || "",
      tcNo: source.tcNo || "",
      gender: source.gender || "",
      birthDay: source.birthDay ? String(source.birthDay).slice(0, 10) : "",
    });
    setPhysical({
      BMI: source.BMI ?? "",
      bloodType: source.bloodType || "",
    });
    setAddress({
      city: source.address?.city || "",
      district: source.address?.district || "",
      fullAddress: source.address?.fullAddress || "",
      postalCode: source.address?.postalCode || "",
    });
    setAllergies(Array.isArray(source.allergies) ? source.allergies : []);
    setActiveDiseases(
      Array.isArray(source.chronicDiseases?.activeDiseases)
        ? source.chronicDiseases.activeDiseases
        : []
    );
    setPastDiseases(
      Array.isArray(source.chronicDiseases?.pastDiseases)
        ? source.chronicDiseases.pastDiseases
        : []
    );
    setPickerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?._id]);

  // ── Ortak mutation runner ─────────────────────────────────────────
  const runMutation = async (mutationThunk) => {
    setSaveStatus("saving");
    onSaveStatusChange?.("saving");
    try {
      const promise = mutationThunk();
      const wrapped = registerAutoSave ? registerAutoSave(promise) : promise;
      const result = await wrapped;
      setSaveStatus("saved");
      onSaveStatusChange?.("saved");
      return result;
    } catch (err) {
      setSaveStatus("error");
      onSaveStatusChange?.("error");
      throw err;
    }
  };

  // ── Section save'leri ─────────────────────────────────────────────
  // Otomatik kayıt: "Kaydet" butonu yok; alanlar değişince debounce ile
  // sessizce kaydedilir (başarıda toast yok, hatada gösterilir).
  const saveGeneral = async () => {
    try {
      const result = await runMutation(() =>
        updatePatient({ token, patientId, ...general }).unwrap()
      );
      onPatientSaved?.(result?.data?.patient);
    } catch (err) {
      showError(err?.data?.message || "Kimlik bilgileri kaydedilemedi.");
    }
  };

  const savePhysical = async () => {
    try {
      // BMI form alanından string olarak gelir; backend Number bekliyor —
      // boş bırakılırsa null, geçerli sayı varsa Number, geçersizse undefined
      // göndererek 400 dönmesini engelle.
      const bmiRaw = String(physical.BMI ?? "").trim();
      let bmiPayload;
      if (bmiRaw === "") bmiPayload = null;
      else {
        const n = Number(bmiRaw);
        bmiPayload = Number.isFinite(n) ? n : undefined;
      }
      // bloodType enum-validated; boş string Mongoose update validator'ında
      // "Geçerli bir kan grubu seçiniz" hatası attırır. Boşsa alanı komple
      // payload'a koyma — ya enum değeri ya da hiç gönderme.
      const btRaw = String(physical.bloodType ?? "").trim();
      const btPayload = btRaw === "" ? undefined : btRaw;
      const result = await runMutation(() =>
        updatePatient({
          token,
          patientId,
          ...(bmiPayload !== undefined ? { BMI: bmiPayload } : {}),
          ...(btPayload !== undefined ? { bloodType: btPayload } : {}),
        }).unwrap()
      );
      onPatientSaved?.(result?.data?.patient);
    } catch (err) {
      showError(err?.data?.message || "Sağlık bilgileri kaydedilemedi.");
    }
  };

  const saveAddress = async () => {
    try {
      // Formda olmayan alt alanlar (country) korunur.
      const next = { ...(source?.address || {}) };
      ADDRESS_KEYS.forEach((k) => {
        next[k] = String(address[k] ?? "").trim();
      });
      const result = await runMutation(() =>
        updatePatient({ token, patientId, address: next }).unwrap()
      );
      onPatientSaved?.(result?.data?.patient);
    } catch (err) {
      showError(err?.data?.message || "Adres kaydedilemedi.");
    }
  };

  // BMI hesaplama drawer'ı — hesaplanan değer debounce beklemeden kaydedilir.
  const saveCalculatedBmi = async (value) => {
    setBmiSaving(true);
    try {
      const result = await runMutation(() =>
        updatePatient({ token, patientId, BMI: value }).unwrap()
      );
      setPhysical((prev) => ({ ...prev, BMI: String(value) }));
      onPatientSaved?.(result?.data?.patient);
      showSuccess("BMI kaydedildi.");
      setBmiDrawerOpen(false);
    } catch (err) {
      showError(err?.data?.message || "BMI kaydedilemedi.");
    } finally {
      setBmiSaving(false);
    }
  };

  // ── Otomatik kayıt (debounce) — Kimlik + Sağlık + Adres ───────────
  // Alan değişince ~800ms sessizlikte kaydedilir. Session'da Muayene
  // Tamamla flush'ı + unmount fire-and-forget korunur.
  const generalDirtyRef = React.useRef(false);
  const physicalDirtyRef = React.useRef(false);
  const addressDirtyRef = React.useRef(false);
  const markGeneralDirty = () => {
    generalDirtyRef.current = true;
  };
  const markPhysicalDirty = () => {
    physicalDirtyRef.current = true;
  };
  const markAddressDirty = () => {
    addressDirtyRef.current = true;
  };

  // Güncel save fonksiyonlarına ref ile eriş (flush/unmount eski kapanışı
  // çağırmasın).
  const saveGeneralRef = React.useRef(saveGeneral);
  saveGeneralRef.current = saveGeneral;
  const savePhysicalRef = React.useRef(savePhysical);
  savePhysicalRef.current = savePhysical;
  const saveAddressRef = React.useRef(saveAddress);
  saveAddressRef.current = saveAddress;

  // Kimlik debounce (kimlik salt-okunur değilse)
  useEffect(() => {
    if (genReadOnly) return undefined;
    if (!generalDirtyRef.current) return undefined;
    const t = setTimeout(() => {
      generalDirtyRef.current = false;
      saveGeneral();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [general]);

  // Sağlık debounce (düzenlenebilirse)
  useEffect(() => {
    if (readOnly) return undefined;
    if (!physicalDirtyRef.current) return undefined;
    const t = setTimeout(() => {
      physicalDirtyRef.current = false;
      savePhysical();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [physical.BMI, physical.bloodType]);

  // Adres debounce (düzenlenebilirse)
  useEffect(() => {
    if (readOnly) return undefined;
    if (!addressDirtyRef.current) return undefined;
    const t = setTimeout(() => {
      addressDirtyRef.current = false;
      saveAddress();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  // Source yeniden yüklenince dirty flag'leri sıfırla
  useEffect(() => {
    generalDirtyRef.current = false;
    physicalDirtyRef.current = false;
    addressDirtyRef.current = false;
  }, [source?._id]);

  // Muayene Tamamla flush (session) — bekleyen dirty save'leri hemen yaz
  useEffect(() => {
    if (!registerFlush) return undefined;
    const unregister = registerFlush(async () => {
      if (generalDirtyRef.current) {
        generalDirtyRef.current = false;
        await saveGeneralRef.current?.();
      }
      if (physicalDirtyRef.current) {
        physicalDirtyRef.current = false;
        await savePhysicalRef.current?.();
      }
      if (addressDirtyRef.current) {
        addressDirtyRef.current = false;
        await saveAddressRef.current?.();
      }
    });
    return unregister;
  }, [registerFlush]);

  // Unmount: 800ms debounce dolmadıysa bekleyen değişiklikleri fire-and-forget
  // gönder (sekme değişimi / sayfadan çıkış).
  useEffect(() => {
    return () => {
      [
        [generalDirtyRef, saveGeneralRef],
        [physicalDirtyRef, savePhysicalRef],
        [addressDirtyRef, saveAddressRef],
      ].forEach(([dirtyRef, saveRef]) => {
        if (!dirtyRef.current) return;
        dirtyRef.current = false;
        try {
          saveRef.current?.();
        } catch (_) {}
      });
    };
  }, []);

  // ── Alerji / kronik hastalık ──────────────────────────────────────
  const saveAllergies = async (next) => {
    const previous = allergies;
    setAllergies(next);
    try {
      const result = await runMutation(() =>
        updatePatient({ token, patientId, allergies: next }).unwrap()
      );
      onPatientSaved?.(result?.data?.patient);
      showSuccess("Alerji listesi güncellendi.");
      return true;
    } catch (err) {
      setAllergies(previous);
      showError(err?.data?.message || "Alerji kaydedilemedi.");
      return false;
    }
  };

  const saveChronic = async (nextActive, nextPast) => {
    const previousActive = activeDiseases;
    const previousPast = pastDiseases;
    setActiveDiseases(nextActive);
    setPastDiseases(nextPast);
    try {
      const result = await runMutation(() =>
        updatePatient({
          token,
          patientId,
          chronicDiseases: {
            activeDiseases: nextActive,
            pastDiseases: nextPast,
          },
        }).unwrap()
      );
      onPatientSaved?.(result?.data?.patient);
      showSuccess("Kronik hastalıklar güncellendi.");
      return true;
    } catch (err) {
      setActiveDiseases(previousActive);
      setPastDiseases(previousPast);
      showError(err?.data?.message || "Kronik hastalık kaydedilemedi.");
      return false;
    }
  };

  // Drawer'dan gelen seçimleri ilgili listeye ekle (tekrarları atla)
  const appendUnique = (list, values) => {
    const seen = new Set(list.map((v) => v.toLocaleLowerCase("tr-TR")));
    const next = [...list];
    values.forEach((v) => {
      const key = String(v || "").trim().toLocaleLowerCase("tr-TR");
      if (!key || seen.has(key)) return;
      seen.add(key);
      next.push(String(v).trim());
    });
    return next;
  };

  const handlePickerSubmit = async (values) => {
    let ok = false;
    if (picker === "allergy") ok = await saveAllergies(appendUnique(allergies, values));
    else if (picker === "active")
      ok = await saveChronic(appendUnique(activeDiseases, values), pastDiseases);
    else if (picker === "past")
      ok = await saveChronic(activeDiseases, appendUnique(pastDiseases, values));
    if (ok) setPickerOpen(false);
  };

  // ── Seçim drawer'ı veri kaynakları (sunucuda sayfalı arama) ────────
  const fetchAllergies = useCallback(
    async ({ query, page }) => {
      const res = await listAllergies({
        token,
        status: "active",
        query: query || undefined,
        page,
        limit: PICKER_PAGE_SIZE,
      }).unwrap();
      const items = (res?.data?.items || [])
        .filter((a) => a?.name)
        .map((a) => ({ key: a._id || a.name, value: a.name, title: a.name }));
      return { items, hasMore: page < (res?.data?.pagination?.pages || 0) };
    },
    [listAllergies, token]
  );

  const fetchDiagnoses = useCallback(
    async ({ query, page }) => {
      const res = await listDiagnoses({
        token,
        status: "active",
        query: query || undefined,
        page,
        limit: PICKER_PAGE_SIZE,
      }).unwrap();
      const items = (res?.data?.diagnoses || [])
        .map((d) => ({
          key: d._id || buildDiagnosisLabel(d),
          value: buildDiagnosisLabel(d),
          title: d.name || buildDiagnosisLabel(d),
          subtitle: d.code || undefined,
        }))
        .filter((o) => o.value);
      return { items, hasMore: page < (res?.data?.pagination?.pages || 0) };
    },
    [listDiagnoses, token]
  );

  // ── Etiket kartları ───────────────────────────────────────────────
  const TAG_SECTIONS = {
    allergy: {
      title: "Alerjiler",
      subtitle: "Bilinen ilaç ve madde alerjileri",
      Icon: WarningAmberRounded,
      accent: COLORS.warning,
      iconColor: COLORS.warningDark,
      items: allergies,
      onRemove: (idx) => saveAllergies(allergies.filter((_, i) => i !== idx)),
      emptyTitle: "Bilinen alerji yok",
      emptyHint: "Alerjisi varsa Ekle ile listeden seçin.",
      drawerTitle: "Alerji Ekle",
      drawerSubtitle: `${terms.patientGen} bilinen alerjilerini seçin`,
      fetchPage: fetchAllergies,
      searchPlaceholder: "Alerji ara",
      noun: "alerji",
    },
    active: {
      title: "Aktif Kronik Hastalıklar",
      subtitle: "Süren ve takip edilen tanılar",
      Icon: MonitorHeartRounded,
      accent: COLORS.error,
      iconColor: COLORS.errorDark,
      withCode: true,
      items: activeDiseases,
      onRemove: (idx) =>
        saveChronic(activeDiseases.filter((_, i) => i !== idx), pastDiseases),
      emptyTitle: "Aktif kronik hastalık yok",
      emptyHint: "Süren bir tanı varsa Ekle ile listeden seçin.",
      drawerTitle: "Aktif Kronik Hastalık Ekle",
      drawerSubtitle: "Süren tanıları ICD listesinden seçin",
      fetchPage: fetchDiagnoses,
      searchPlaceholder: "Tanı adı veya kodu ara",
      noun: "tanı",
    },
    past: {
      title: "Geçmiş Kronik Hastalıklar",
      subtitle: "Geçirilmiş, artık aktif olmayan tanılar",
      Icon: HistoryRounded,
      accent: COLORS.slate500,
      iconColor: COLORS.slate600,
      withCode: true,
      items: pastDiseases,
      onRemove: (idx) =>
        saveChronic(activeDiseases, pastDiseases.filter((_, i) => i !== idx)),
      emptyTitle: "Geçmiş kronik hastalık yok",
      emptyHint: "Geçirilmiş bir tanı varsa Ekle ile listeden seçin.",
      drawerTitle: "Geçmiş Kronik Hastalık Ekle",
      drawerSubtitle: "Geçirilmiş tanıları ICD listesinden seçin",
      fetchPage: fetchDiagnoses,
      searchPlaceholder: "Tanı adı veya kodu ara",
      noun: "tanı",
    },
  };

  // Kart içi düzenli satır: ikon kutusu · (ICD kodu) · ad · kaldır
  const renderTagRow = (s, item, idx, isLast) => {
    const { Icon } = s;
    const { code, name } = s.withCode ? splitDiagnosis(item) : { code: null, name: item };
    return (
      <Box
        key={`${item}-${idx}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: CARD_PAD,
          py: 1,
          minHeight: 48,
          borderBottom: isLast ? "none" : `1px solid ${COLORS.slate100}`,
          "&:hover": { bgcolor: alpha(COLORS.slate50, 0.8) },
          "&:hover .tag-remove, & .tag-remove:focus-visible": { opacity: 1 },
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(s.accent, 0.1),
            color: s.iconColor,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        {s.withCode ? (
          <Typography
            component="span"
            sx={{
              minWidth: 52,
              flexShrink: 0,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              textAlign: "center",
              fontSize: TYPO.label,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: code ? COLORS.slate600 : COLORS.slate300,
              bgcolor: COLORS.slate100,
            }}
          >
            {code || "—"}
          </Typography>
        ) : null}
        <Typography
          title={name}
          noWrap
          sx={{ flex: 1, minWidth: 0, fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800 }}
        >
          {name}
        </Typography>
        {readOnly ? null : (
          <IconButton
            className="tag-remove"
            size="small"
            aria-label={`${name} kaldır`}
            onClick={() => s.onRemove(idx)}
            disabled={isSaving}
            sx={{
              flexShrink: 0,
              opacity: { xs: 1, md: 0.45 },
              color: COLORS.slate400,
              transition: "opacity .15s, color .15s",
              "&:hover": { color: COLORS.errorDark, bgcolor: alpha(COLORS.error, 0.08) },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderTagCard = (key) => {
    const s = TAG_SECTIONS[key];
    const { Icon } = s;
    return (
      <SectionCard
        key={key}
        title={s.items.length ? `${s.title} · ${s.items.length}` : s.title}
        subtitle={s.subtitle}
        icon={<Icon sx={{ fontSize: 18 }} />}
        accent={s.accent}
        sx={cardSx}
        action={
          readOnly || loading ? null : (
            <ActionButton
              variant="add"
              compact
              palette={palette}
              label="Ekle"
              aria-label={s.drawerTitle}
              onClick={() => openPicker(key)}
              disabled={isSaving}
            />
          )
        }
      >
        <Box
          sx={{
            height: TAG_CARD_BODY_HEIGHT,
            minHeight: 0,
            // Boşken kaydırma kabı değil: tekerlek sayfaya geçer, çizim ortada durur
            overflowY: !loading && s.items.length === 0 ? "visible" : "auto",
            overscrollBehavior: !loading && s.items.length === 0 ? "auto" : "contain",
          }}
        >
          {loading ? (
            [0, 1, 2].map((i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: CARD_PAD, py: 1, minHeight: 48 }}>
                <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.5 }} />
                {s.withCode ? <Skeleton variant="rounded" width={52} height={20} /> : null}
                <Skeleton variant="text" width={`${40 + i * 15}%`} height={20} />
              </Box>
            ))
          ) : s.items.length === 0 ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EmptyState
                variant="illustrated-sm"
                icon={<Icon />}
                title={s.emptyTitle}
                description={readOnly ? undefined : s.emptyHint}
                sx={{ py: 0 }}
              />
            </Box>
          ) : (
            s.items.map((item, idx) => renderTagRow(s, item, idx, idx === s.items.length - 1))
          )}
        </Box>
      </SectionCard>
    );
  };

  // ── Küçük alan yardımcıları ───────────────────────────────────────
  const textField = ({ value, onChange, disabled, ...rest }) => (
    <FormTextField
      palette={palette}
      variant="standard"
      size="small"
      fullWidth
      value={value}
      onChange={onChange}
      disabled={disabled}
      InputProps={{ disableUnderline: disabled }}
      {...rest}
    />
  );

  const setGeneralField = (key) => (e) => {
    markGeneralDirty();
    setGeneral((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const setAddressField = (key) => (e) => {
    markAddressDirty();
    setAddress((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const age = ageOf(general.birthDay);
  const bmi = bmiCategory(physical.BMI);
  const activePicker = picker ? TAG_SECTIONS[picker] : null;

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
      {/* Otomatik kayıt göstergesi — sağ üstte sabit */}
      <SaveStatusIndicator status={saveStatus} palette={palette} />

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" },
          alignItems: "stretch",
        }}
      >
        {/* Kimlik ve İletişim — muayenede salt okunur */}
        <SectionCard
          title="Kimlik ve İletişim"
          subtitle={
            usesApp
              ? `${terms.patient} telefon ve e-postasını uygulamadan değiştirir; diğer alanlar düzenlenebilir`
              : `${terms.patientGen} kimlik ve ulaşım bilgileri`
          }
          icon={<BadgeRounded sx={{ fontSize: 18 }} />}
          sx={cardSx}
        >
          <Row loading={loading} label="Ad">
            {textField({ value: general.name, onChange: setGeneralField("name"), disabled: identityReadOnly })}
          </Row>
          <Row loading={loading} label="Soyad">
            {textField({ value: general.surname, onChange: setGeneralField("surname"), disabled: identityReadOnly })}
          </Row>
          <Row loading={loading} label="TC Kimlik">
            {textField({
              value: general.tcNo,
              onChange: setGeneralField("tcNo"),
              disabled: identityReadOnly,
              inputProps: { inputMode: "numeric", maxLength: 11, style: { fontVariantNumeric: "tabular-nums" } },
            })}
          </Row>
          <Row loading={loading} label="Doğum Tarihi">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormDatePicker
                  palette={palette}
                  variant="standard"
                  fullWidth
                  value={general.birthDay}
                  onChange={(v) => {
                    markGeneralDirty();
                    setGeneral((prev) => ({ ...prev, birthDay: v }));
                  }}
                  maxDate={new Date().toISOString().slice(0, 10)}
                  disabled={identityReadOnly}
                  clearable={!identityReadOnly}
                />
              </Box>
              {age != null ? <FieldHint>{age} yaş</FieldHint> : null}
            </Box>
          </Row>
          <Row loading={loading} label="Cinsiyet">
            <FormSelect
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              value={general.gender}
              onChange={(v) => {
                markGeneralDirty();
                setGeneral((prev) => ({ ...prev, gender: v }));
              }}
              options={GENDER_OPTIONS}
              placeholder="Seçiniz"
              disabled={identityReadOnly}
              InputProps={{ disableUnderline: identityReadOnly }}
            />
          </Row>
          <Row loading={loading} label="Telefon">
            <PhoneInput
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              value={general.tellNo}
              onChange={(v) => {
                markGeneralDirty();
                setGeneral((prev) => ({ ...prev, tellNo: v }));
              }}
              disabled={contactReadOnly}
              InputProps={{ disableUnderline: contactReadOnly }}
            />
          </Row>
          {/* WhatsApp onayı — modül + izin + klinik bağlamı yoksa kendini gizler */}
          {!loading ? <WhatsAppConsentRow patientId={patientId} token={token} /> : null}
          <Row loading={loading} label="E-posta" noBorder>
            {textField({
              value: general.email,
              onChange: setGeneralField("email"),
              disabled: contactReadOnly,
              type: "email",
            })}
          </Row>
        </SectionCard>

        <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP, minWidth: 0 }}>
          {/* Sağlık */}
          <SectionCard
            title="Sağlık"
            subtitle="Kan grubu ve vücut kitle indeksi"
            icon={<FavoriteRounded sx={{ fontSize: 18 }} />}
            sx={cardSx}
          >
            {showBloodType && (
            <Row loading={loading} label="Kan Grubu">
              <FormSelect
                palette={palette}
                variant="standard"
                size="small"
                fullWidth
                value={physical.bloodType}
                onChange={(v) => {
                  markPhysicalDirty();
                  setPhysical((prev) => ({ ...prev, bloodType: v }));
                }}
                options={BLOOD_TYPE_OPTIONS}
                placeholder="Seçiniz"
                disabled={readOnly}
                InputProps={{ disableUnderline: readOnly }}
              />
            </Row>
            )}
            <Row loading={loading} label="BMI" noBorder>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormTextField
                    palette={palette}
                    variant="standard"
                    size="small"
                    fullWidth
                    type="number"
                    value={physical.BMI}
                    onChange={(e) => {
                      markPhysicalDirty();
                      setPhysical((prev) => ({ ...prev, BMI: e.target.value }));
                    }}
                    disabled={readOnly}
                    InputProps={{
                      disableUnderline: readOnly,
                      inputProps: { step: "0.1", min: 0, "aria-label": "BMI" },
                    }}
                  />
                </Box>
                {bmi ? <FieldHint color={bmi.color}>{bmi.label}</FieldHint> : null}
                {!readOnly ? (
                  <Tooltip title="Boy ve kilodan hesapla">
                    <IconButton
                      size="small"
                      aria-label="Boy ve kilodan BMI hesapla"
                      onClick={() => setBmiDrawerOpen(true)}
                      // Sabit kare + flex ortalama; negatif dikey margin satır
                      // yüksekliğini input'unkine eşit tutar (etiket hizası bozulmaz).
                      sx={{
                        width: 26,
                        height: 26,
                        p: 0,
                        my: "-2px",
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: palette.primary,
                        bgcolor: alpha(palette.primary, 0.08),
                        "&:hover": { bgcolor: alpha(palette.primary, 0.16) },
                      }}
                    >
                      <CalculateRounded sx={{ fontSize: 16, display: "block" }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            </Row>
          </SectionCard>

          {/* Adres */}
          <SectionCard
            title="Adres"
            subtitle={`${terms.patient} dağılımındaki ilçe listesi buradan beslenir`}
            icon={<PlaceRounded sx={{ fontSize: 18 }} />}
            sx={{ ...cardSx, flex: 1 }}
          >
            <Row loading={loading} label="İl">
              {textField({ value: address.city, onChange: setAddressField("city"), disabled: readOnly, placeholder: readOnly ? "" : "Ör. İstanbul" })}
            </Row>
            <Row loading={loading} label="İlçe">
              {textField({ value: address.district, onChange: setAddressField("district"), disabled: readOnly, placeholder: readOnly ? "" : "Ör. Kadıköy" })}
            </Row>
            <Row loading={loading} label="Açık Adres">
              {textField({
                value: address.fullAddress,
                onChange: setAddressField("fullAddress"),
                disabled: readOnly,
                multiline: true,
                maxRows: 3,
                placeholder: readOnly ? "" : "Mahalle, sokak, bina ve daire no",
              })}
            </Row>
            <Row loading={loading} label="Posta Kodu" noBorder>
              {textField({
                value: address.postalCode,
                onChange: setAddressField("postalCode"),
                disabled: readOnly,
                inputProps: { inputMode: "numeric", maxLength: 5 },
              })}
            </Row>
          </SectionCard>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr" },
        }}
      >
        {renderTagCard("allergy")}
        {showChronic && renderTagCard("active")}
        {showChronic && renderTagCard("past")}
      </Box>

      <BmiCalculatorDrawer
        open={bmiDrawerOpen}
        onClose={() => setBmiDrawerOpen(false)}
        onSave={saveCalculatedBmi}
        saving={bmiSaving}
      />
      <TagPickerDrawer
        open={pickerOpen && Boolean(activePicker)}
        onClose={() => setPickerOpen(false)}
        icon={activePicker ? <activePicker.Icon /> : null}
        accent={activePicker?.accent}
        title={activePicker?.drawerTitle}
        subtitle={activePicker?.drawerSubtitle}
        fetchPage={activePicker?.fetchPage}
        existing={activePicker?.items || []}
        searchPlaceholder={activePicker?.searchPlaceholder}
        noun={activePicker?.noun}
        loading={isSaving}
        onSubmit={handlePickerSubmit}
      />
    </Box>
  );
}
